// Step 2 — Primary goal + inbox AI mode (US-018, US-019).
//
// Pixel-faithful to `prototypes_and_docs/ls-onboarding-v2.jsx:154-247`. The
// inbox-mode picker is *conditionally* revealed when the goal involves the
// inbox (`inbox` or `both`). The Continue CTA is disabled until both
// answers are present.

import { ArrowRight, ChevronLeft } from 'lucide-react';

import { useT } from '@barel/i18n';
import { Button, OnboardingStep, RadioCardGroup } from '@barel/ui';

import type { OnboardingGoal, DefaultInboxMode } from '@barel/core';

interface GoalProps {
  goal: OnboardingGoal | null;
  inboxMode: DefaultInboxMode | null;
  onGoalChange: (goal: OnboardingGoal) => void;
  onInboxModeChange: (mode: DefaultInboxMode) => void;
  onBack: () => void;
  onContinue: () => void;
}

export function Goal({
  goal,
  inboxMode,
  onGoalChange,
  onInboxModeChange,
  onBack,
  onContinue,
}: GoalProps) {
  const t = useT('onboarding');

  const goalOptions = [
    {
      value: 'inbox' as const,
      title: t('step2.goals.inbox.title'),
      description: t('step2.goals.inbox.body'),
      icon: <span aria-hidden>{t('step2.goals.inbox.icon')}</span>,
    },
    {
      value: 'campaigns' as const,
      title: t('step2.goals.campaigns.title'),
      description: t('step2.goals.campaigns.body'),
      icon: <span aria-hidden>{t('step2.goals.campaigns.icon')}</span>,
    },
    {
      value: 'both' as const,
      title: t('step2.goals.both.title'),
      description: t('step2.goals.both.body'),
      icon: <span aria-hidden>{t('step2.goals.both.icon')}</span>,
      badge: t('step2.recommended'),
    },
  ];

  const modeOptions = [
    {
      value: 'auto' as const,
      title: t('step2.modes.auto.title'),
      description: t('step2.modes.auto.body'),
      icon: <span aria-hidden>{t('step2.modes.auto.icon')}</span>,
    },
    {
      value: 'review' as const,
      title: t('step2.modes.review.title'),
      description: t('step2.modes.review.body'),
      icon: <span aria-hidden>{t('step2.modes.review.icon')}</span>,
      badge: t('step2.recommended'),
    },
  ];

  const needsMode = goal === 'inbox' || goal === 'both';
  const canContinue = !!goal && (!needsMode || !!inboxMode);

  return (
    <OnboardingStep
      width="sm"
      eyebrow={t('step2.eyebrow')}
      title={t('step2.title')}
      description={t('step2.subtitle')}
      primary={
        <Button
          type="button"
          variant="primary"
          size="lg"
          fullWidth
          disabled={!canContinue}
          onClick={onContinue}
          iconRight={<ArrowRight aria-hidden style={{ width: 14, height: 14 }} />}
          data-testid="onboarding-step2-continue"
        >
          {t('wizard.continue')}
        </Button>
      }
      secondary={
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onBack}
          iconLeft={<ChevronLeft aria-hidden style={{ width: 14, height: 14 }} />}
          data-testid="onboarding-step2-back"
        >
          {t('wizard.back')}
        </Button>
      }
    >
      <RadioCardGroup
        name="onboarding-goal"
        aria-label={t('step2.title')}
        value={goal}
        onValueChange={onGoalChange}
        options={goalOptions}
      />

      {needsMode ? (
        <div
          data-region="onboarding-step2-inbox-mode"
          className="flex flex-col gap-3 border-t border-dashed border-default pt-4"
        >
          <div className="flex flex-col gap-1">
            <span className="text-2xs font-bold uppercase tracking-wider text-accent">
              {t('step2.modeEyebrow')}
            </span>
            <span className="text-xs leading-relaxed text-secondary">
              {t('step2.modeSubtitle')}
            </span>
          </div>
          <RadioCardGroup
            name="onboarding-inbox-mode"
            aria-label={t('step2.modeEyebrow')}
            value={inboxMode}
            onValueChange={onInboxModeChange}
            options={modeOptions}
          />
        </div>
      ) : null}
    </OnboardingStep>
  );
}
